import type { BackendRouteDefinition } from '../types';

// All four AI-chat related routes live in a single module so the Sub-phase 5
// work blocks that flip these routes (S5-WB7 for the non-streaming pair and
// cancel; S5-WB8 for the streaming flip to `api-stream`) edit one owned file
// instead of contending with other Phase 4 long-running migrations.
//
// Phase 4 / Sub-phase 5 work block S5-WB7 (PRA-767) flipped the
// non-streaming and cancel routes to the `api` transport. S5-WB8 (PRA-768)
// flipped `openaiChatStreaming` to `api-stream` (SSE-over-HTTP). All four
// routes now travel through the Firebase Hosting `/api/ai/**` rewrite to
// `api-longrun-node-prod`, which has the 300 s timeout and `minInstances=1`
// warm-instance profile the AI chat workload needs. The legacy callables
// were retired by S7-WB2 (PRA-773); the only live handlers are now the
// `api-longrun-node` plugins.
//
// `path` is the route's leaf — `apiBaseUrl` (see
// `shared/backend/config.ts` → `resolveApiBaseUrl`) contributes the `/api`
// prefix at invoke time. A leading `/api/` in `path` would produce
// `/api/api/ai/chat` and miss the Vite dev proxy / Hosting rewrite target.
//
// `body: (requestData) => requestData` is intentional. The wire shape on
// the new HTTP route already matches the firms-app's `ScoutChatRequestData`
// (`{ messages: Array<{ role, content }> }`), so passing the request data
// through unchanged keeps the migration mechanical and gives the typecheck a
// chance to flag a future shape drift between the two ends.

interface ScoutChatRequestData {
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
  }>;
  conversationId?: string;
}

interface CancelOpenAIChatRequestData {
  threadId: string;
  runId: string;
}

interface ListScoutConversationsRequestData {
  limit?: number;
}

interface GetScoutConversationMessagesRequestData {
  conversationId: string;
}

interface DeleteScoutConversationRequestData {
  conversationId: string;
}

export const openaiChatRoute = {
  transport: 'api',
  method: 'POST',
  path: '/ai/chat',
  body: (requestData: ScoutChatRequestData) => requestData,
} as const satisfies BackendRouteDefinition;

// `openaiChatNonStreaming` is the firms app's fallback path that fires when
// SSE setup fails for the streaming variant. Both the streaming and
// non-streaming HTTP routes are served by `api-longrun-node` and share
// thread state through Firestore (`firms/{firmId}/scoutThreads/{userId}`),
// so a fallback message lands in the same conversation history the user
// was building over the streaming transport. This route still points at
// `/ai/chat` (the non-streaming endpoint) — `openaiChatStreamingRoute`
// below owns the SSE leaf.
export const openaiChatNonStreamingRoute = {
  transport: 'api',
  method: 'POST',
  path: '/ai/chat',
  body: (requestData: ScoutChatRequestData) => requestData,
} as const satisfies BackendRouteDefinition;

// Flipped from `callableStream` to `api-stream` by S5-WB8 (PRA-768) — the
// SSE-over-HTTP route lives at `POST /api/ai/chat/stream` on
// api-longrun-node (the `apiBaseUrl` prefix supplies `/api`, so the leaf
// here is `/ai/chat/stream` to match the comment above on the `/api/`
// double-prefix gotcha). The Firebase `openaiChatStreaming` callable was
// retired by S7-WB2 (PRA-773); `openaiChatNonStreamingRoute` above is the
// SSE-fails fallback that hits the non-streaming HTTP route.
export const openaiChatStreamingRoute = {
  transport: 'api-stream',
  method: 'POST',
  path: '/ai/chat/stream',
  body: (requestData: ScoutChatRequestData) => requestData,
} as const satisfies BackendRouteDefinition;

export const cancelOpenAIChatRoute = {
  transport: 'api',
  method: 'POST',
  path: '/ai/chat/cancel',
  body: (requestData: CancelOpenAIChatRequestData) => requestData,
} as const satisfies BackendRouteDefinition;

export const listScoutConversationsRoute = {
  transport: 'api',
  method: 'GET',
  path: '/ai/conversations',
  query: (requestData: ListScoutConversationsRequestData = {}) => ({
    limit: requestData.limit,
  }),
} as const satisfies BackendRouteDefinition;

export const getScoutConversationMessagesRoute = {
  transport: 'api',
  method: 'GET',
  path: '/ai/conversations/:conversationId/messages',
  params: (requestData: GetScoutConversationMessagesRequestData) => ({
    conversationId: requestData.conversationId,
  }),
} as const satisfies BackendRouteDefinition;

export const createScoutConversationRoute = {
  transport: 'api',
  method: 'POST',
  path: '/ai/conversations',
} as const satisfies BackendRouteDefinition;

export const deleteScoutConversationRoute = {
  transport: 'api',
  method: 'POST',
  path: '/ai/conversations/delete',
  body: (requestData: DeleteScoutConversationRequestData) => requestData,
} as const satisfies BackendRouteDefinition;
